<template>
  <div class="row wrapper border-bottom white-bg page-heading">
    <div class="col-lg-10">
      <h2>KSeF - szczegóły sprzedawcy</h2>
      <ol class="breadcrumb">
        <li>
          <router-link to="/">Home</router-link>
        </li>
        <li>
          <router-link to="/ksef/companies">KSeF - sprzedawcy</router-link>
        </li>
        <li class="active">
          <strong>{{ companyData.name }}</strong>
        </li>
      </ol>
    </div>
  </div>

  <div class="wrapper wrapper-content animated fadeInRight" v-if="!loading">
    <div class="row">
      <div class="col-lg-12">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Dane sprzedawcy i połączenie z KSeF</h5>
            <span class="ibox-toggle" @click="isMainOpen = !isMainOpen">
              <i :class="chevronMainClass"></i>
            </span>
          </div>
          <div class="ibox-content" v-show="isMainOpen">
            <div class="row">
              <div class="col-lg-12">
                <div class="form-group">
                  <router-link
                    v-if="canUpdateCompany"
                    :to="`/ksef/companies/${companyData.pk}/update`"
                  >
                    <button class="btn btn-primary" type="button">
                      Edytuj
                    </button>
                  </router-link>
                  <button
                    class="btn btn-warning"
                    type="button"
                    v-if="!isConnectionActive && canUpdateCompany"
                    :disabled="!hasActiveCertificate || activatingConnection"
                    @click="activateConnection"
                  >
                    Aktywuj połączenie z KSeF
                  </button>
                </div>
              </div>
            </div>
            <div class="row company-info">
              <div class="col-md-6">
                <p>
                  <strong>Nazwa:</strong>
                  <span>{{ companyData.name }}</span>
                </p>
                <p>
                  <strong>NIP:</strong>
                  <span>{{ companyData.nip }}</span>
                </p>
                <p>
                  <strong>Data utworzenia:</strong>
                  <span>{{ companyData.date_added }}</span>
                </p>
                <p>
                  <strong>Utworzono przez:</strong>
                  <span>{{ companyData.created_by }}</span>
                </p>
                <p>
                  <strong>Data modyfikacji:</strong>
                  <span>{{ companyData.date_modified }}</span>
                </p>
                <p>
                  <strong>Zmodyfikowano przez:</strong>
                  <span>{{ companyData.last_modified_by }}</span>
                </p>
              </div>
              <div class="col-md-6">
                <div
                  class="alert connection-status"
                  :class="
                    isConnectionActive ? 'alert-success' : 'alert-warning'
                  "
                >
                  <strong>Status połączenia:</strong>
                  <span v-if="isConnectionActive">Aktywne</span>
                  <span v-else>Nieaktywne</span>
                </div>
                <p>
                  <strong>Token dostępowy ważny do:</strong>
                  <span v-if="companyData.access_token_valid_until">
                    {{ companyData.access_token_valid_until }}
                  </span>
                  <span v-else>brak aktywnego tokenu</span>
                </p>
                <p>
                  <strong>Token odświeżający ważny do:</strong>
                  <span v-if="companyData.refresh_token_valid_until">
                    {{ companyData.refresh_token_valid_until }}
                  </span>
                  <span v-else>brak aktywnego tokenu</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-12">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Przypisane certyfikaty</h5>
            <span class="ibox-toggle" @click="isCertsOpen = !isCertsOpen">
              <i :class="chevronCertsClass"></i>
            </span>
          </div>
          <div class="ibox-content" v-show="isCertsOpen">
            <router-link
              v-if="canAddCert"
              :to="`/ksef/companies/${routePk}/create-cert`"
            >
              <button class="btn btn-sm btn-primary">Dodaj</button>
            </router-link>
            <div class="row">
              <div class="col-sm-12 m-b-xs">
                <div class="pagination" v-if="certsAll">
                  <span class="items-all">
                    Ilość rekordów: <b>{{ certsAll }}</b>
                  </span>
                  <span>
                    Ilość na stronę:
                    <select v-model="certsServerOptions.rowsPerPage">
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </span>
                  <span>
                    <input type="number" v-model="certsServerOptions.page" /> -
                    {{ Math.ceil(certsAll / certsServerOptions.rowsPerPage) }}
                  </span>
                  <button
                    @click="certsServerOptions.page = 1"
                    class="btn btn-primary ml-25"
                  >
                    Pierwsza
                  </button>
                  <button
                    @click="
                      certsServerOptions.page = certsServerOptions.page - 1
                    "
                    :disabled="certsServerOptions.page === 1"
                    class="prev nl"
                  ></button>
                  <button
                    @click="
                      certsServerOptions.page = certsServerOptions.page + 1
                    "
                    :disabled="
                      certsServerOptions.page ===
                      Math.ceil(certsAll / certsServerOptions.rowsPerPage)
                    "
                    class="next"
                  ></button>
                  <button
                    @click="
                      certsServerOptions.page = Math.ceil(
                        certsAll / certsServerOptions.rowsPerPage
                      )
                    "
                    class="btn btn-primary"
                  >
                    Ostatnia
                  </button>
                </div>
                <EasyDataTable
                  v-model:server-options="certsServerOptions"
                  :server-items-length="certsAll"
                  :headers="certsHeaders"
                  :items="certs"
                  :loading="loadingCerts"
                  alternating
                  border-cell
                  class="table-search"
                >
                  <template #item-is_active="item">
                    {{ item.is_active ? "Tak" : "Nie" }}
                  </template>
                  <template #item-actions="item">
                    <button
                      v-if="canDeleteCert"
                      class="btn btn-danger btn-xs"
                      title="Usuń"
                      @click="
                        showModal = item.pk;
                        deleteName =
                          item.label + ' (' + item.serial_number + ')';
                      "
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </template>
                </EasyDataTable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="wrapper wrapper-content animated fadeInRight" v-if="loading">
    <div class="spiner-example">
      <div class="sk-spinner sk-spinner-wave">
        <div class="sk-rect1"></div>
        <div class="sk-rect2"></div>
        <div class="sk-rect3"></div>
        <div class="sk-rect4"></div>
        <div class="sk-rect5"></div>
      </div>
    </div>
  </div>

  <Modal
    v-if="showModal"
    :title="'Potwierdzenie usunięcia'"
    :content="'Czy na pewno chcesz usunąć <b>' + deleteName + '</b> ?'"
    @callbackOk="deleteCert(showModal)"
    @callbackClose="showModal = false"
  />
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import Modal from "../caffeprint/Modal.vue";
import { hasPermission } from "../../permissions";
import {
  ksefCompanyPermissionsConst,
  ksefCertificatePermissionsConst,
} from "../../constants/ksefConstants";

export default {
  name: "KsefCompanyDetail",
  components: {
    EasyDataTable,
    Modal,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const routePk = ref(route.params.pk);
    const loading = ref(true);
    const loadingCerts = ref(false);
    const activatingConnection = ref(false);
    const companyData = ref({});
    const certs = ref([]);
    const certsAll = ref(0);
    const showModal = ref(false);
    const deleteName = ref("");
    const isMainOpen = ref(true);
    const isCertsOpen = ref(true);

    const certsServerOptions = ref({
      page: 1,
      rowsPerPage: 25,
    });

    const chevronMainClass = computed(() =>
      isMainOpen.value ? "fa fa-chevron-up" : "fa fa-chevron-down"
    );
    const chevronCertsClass = computed(() =>
      isCertsOpen.value ? "fa fa-chevron-up" : "fa fa-chevron-down"
    );

    const canUpdateCompany = computed(() =>
      hasPermission(ksefCompanyPermissionsConst.update)
    );
    const canAddCert = computed(() =>
      hasPermission(ksefCertificatePermissionsConst.add)
    );
    const canDeleteCert = computed(() =>
      hasPermission(ksefCertificatePermissionsConst.delete)
    );

    const certsHeaders = [
      { text: "Nazwa", value: "label" },
      { text: "Aktywny", value: "is_active" },
      { text: "Ważny do", value: "valid_until" },
      { text: "Numer seryjny", value: "serial_number" },
      { text: "Nazwa podmiotu", value: "subject_cn" },
      { text: "Data dodania", value: "date_added" },
      { text: "Utworzono przez", value: "created_by" },
      { text: "Akcje", value: "actions" },
    ];

    const ksefCompanyData = computed(() => store.getters.getKsefCompanyData);
    const ksefCompanyCertsData = computed(
      () => store.getters.getKsefCompanyCertsData
    );
    const activateKsefConnectionData = computed(
      () => store.getters.getActivateKsefConnectionData
    );
    const deleteKsefCertificateData = computed(
      () => store.getters.getDeleteKsefCertificateData
    );

    const hasActiveCertificate = computed(
      () => !!companyData.value.active_certificate
    );
    const isConnectionActive = computed(() => !!companyData.value.is_connected);

    const getCompany = () => {
      loading.value = true;
      store.dispatch("getKsefCompany", { pk: routePk.value });
    };

    const getCompanyCerts = () => {
      loadingCerts.value = true;
      store.dispatch("getKsefCompanyCerts", {
        pk: routePk.value,
        page: certsServerOptions.value.page,
        page_size: certsServerOptions.value.rowsPerPage,
      });
    };

    // Activating the connection rewrites the company tokens - the same
    // permission the edit button requires.
    const activateConnection = () => {
      if (!canUpdateCompany.value) {
        return;
      }
      activatingConnection.value = true;
      store.dispatch("activateKsefConnection", { pk: routePk.value });
    };

    const deleteCert = (pk) => {
      if (!canDeleteCert.value) {
        return;
      }
      showModal.value = false;
      store.dispatch("deleteKsefCertificate", { pk: pk });
    };

    const getErrorMessage = (data, fallback) => {
      if (!data) {
        return fallback;
      }
      if (data.detail) {
        return data.detail;
      }
      if (Array.isArray(data)) {
        return data.length > 0 ? data[0] : fallback;
      }
      const msg = [];
      for (const [key, value] of Object.entries(data)) {
        msg.push(key + ": " + value);
      }
      return msg.length > 0 ? msg[0] : fallback;
    };

    watch(
      () => ksefCompanyData.value,
      function (newValue) {
        if (newValue && newValue.status === 200) {
          companyData.value = newValue.data;
        }
        loading.value = false;
      }
    );

    watch(
      () => ksefCompanyCertsData.value,
      function (newValue) {
        if (newValue && newValue.status === 200) {
          certsAll.value = newValue.data.count;
          certs.value = newValue.data.results || [];
        } else if (newValue && newValue.status) {
          store.dispatch("showToaster", {
            type: "error",
            content: getErrorMessage(
              newValue.data,
              "Nie udało się pobrać certyfikatów!"
            ),
          });
        }
        loadingCerts.value = false;
      }
    );

    watch(
      () => activateKsefConnectionData.value,
      function (newValue) {
        activatingConnection.value = false;
        if (newValue && newValue.status === 200) {
          companyData.value = newValue.data;
          store.dispatch("showToaster", {
            type: "success",
            content: "Połączenie z KSeF zostało aktywowane!",
          });
        } else if (newValue && newValue.status) {
          store.dispatch("showToaster", {
            type: "error",
            content: getErrorMessage(
              newValue.data,
              "Nie udało się aktywować połączenia z KSeF!"
            ),
          });
        }
      }
    );

    watch(
      () => deleteKsefCertificateData.value,
      function (newValue) {
        if (newValue && newValue.status === 204) {
          store.dispatch("showToaster", {
            type: "success",
            content: "Certyfikat został usunięty!",
          });
          getCompany();
          getCompanyCerts();
        } else if (newValue && newValue.status) {
          store.dispatch("showToaster", {
            type: "error",
            content: getErrorMessage(
              newValue.data,
              "Wystąpił błąd podczas usuwania certyfikatu!"
            ),
          });
        }
      }
    );

    watch(
      () => certsServerOptions.value,
      function () {
        getCompanyCerts();
      },
      { deep: true }
    );

    onMounted(() => {
      getCompany();
      getCompanyCerts();
    });

    return {
      routePk,
      loading,
      loadingCerts,
      activatingConnection,
      companyData,
      certs,
      certsAll,
      certsHeaders,
      certsServerOptions,
      hasActiveCertificate,
      isConnectionActive,
      activateConnection,
      deleteCert,
      showModal,
      deleteName,
      isMainOpen,
      isCertsOpen,
      chevronMainClass,
      chevronCertsClass,
      canUpdateCompany,
      canAddCert,
      canDeleteCert,
    };
  },
};
</script>

<style scoped>
.ibox-title {
  position: relative;
}
.ibox-toggle {
  position: absolute;
  right: 15px;
  top: 12px;
  cursor: pointer;
}
.connection-status {
  padding: 8px;
  margin-bottom: 10px;
}
.company-info strong {
  margin-right: 5px;
}
</style>
